/**
 * ProjectPicker — typeahead input bound to GET /projects?q=<prefix>.
 *
 * Suggests existing projects; if the user types a new value, it's passed as
 * projectName so the server creates-or-reuses on POST /meetings.
 *
 * Format guidance:
 *   - OJB project convention is `XX-XXX - Project Name` (e.g. `24-105 - Duke Park`)
 *   - Internal categories (IT, HR, Marketing, etc.) are accepted as-is
 *   - If the typed value is neither an existing project nor matches the
 *     expected format, we flag a non-blocking warning — the user can still
 *     submit the form.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/apiClient.ts';
import { NO_AUTOFILL } from '../../lib/noAutofill.ts';

interface Project {
  id: string;
  name: string;
  usageCount: number;
}

interface Props {
  value: string;
  onChange: (next: { projectId?: string; projectName?: string; label: string }) => void;
  placeholder?: string;
  required?: boolean;
  error?: string;
}

// XX-XXX - Project Name — two digits, hyphen, three digits, space-hyphen-space, name.
const PROJECT_CODE_FORMAT = /^\d{2}-\d{3}\s*-\s*.+$/;

// Well-known internal categories that don't follow the code format.
const INTERNAL_CATEGORY_PREFIXES = ['internal', 'it', 'hr', 'marketing', 'firm', 'admin', 'bd', 'business development'];

function isInternalCategory(name: string): boolean {
  const lower = name.trim().toLowerCase();
  return INTERNAL_CATEGORY_PREFIXES.some((p) => lower === p || lower.startsWith(p + ' ') || lower.startsWith(p + '-'));
}

export function ProjectPicker({
  value,
  onChange,
  placeholder = 'e.g. 24-105 - Duke Park  (or an internal category)',
  required,
  error,
}: Props) {
  const [query, setQuery] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const [isExistingProject, setIsExistingProject] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const suggestions = useQuery({
    queryKey: ['projects', query],
    queryFn: () => api.get<{ projects: Project[] }>('/projects', { q: query, limit: 10 }),
    enabled: query.length > 0 && isOpen,
    staleTime: 5_000,
  });

  const formatIssue = useMemo<string | null>(() => {
    const trimmed = query.trim();
    if (trimmed.length === 0) return null;
    if (isExistingProject) return null; // existing projects are always valid
    if (isInternalCategory(trimmed)) return null;
    if (PROJECT_CODE_FORMAT.test(trimmed)) return null;
    return 'Expected format: XX-XXX - Project Name (e.g. 24-105 - Duke Park). You can still create the draft.';
  }, [query, isExistingProject]);

  function handleChange(v: string) {
    setQuery(v);
    setIsOpen(true);
    setIsExistingProject(false);
    onChange({ projectName: v, label: v });
  }

  function handlePick(p: Project) {
    setQuery(p.name);
    setIsOpen(false);
    setIsExistingProject(true);
    onChange({ projectId: p.id, label: p.name });
  }

  return (
    <div ref={wrapperRef} className="relative">
      <input
        {...NO_AUTOFILL}
        type="text"
        required={required}
        value={query}
        placeholder={placeholder}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => setIsOpen(true)}
        className={
          'w-full rounded border bg-paper-light px-3 py-2 text-sm text-charcoal focus:border-pine focus:outline-none ' +
          (error ? 'border-red-400' : formatIssue ? 'border-yellow-400' : 'border-smoke/40')
        }
      />
      {isOpen && query.length > 0 && suggestions.data && suggestions.data.projects.length > 0 && (
        <ul className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded border border-smoke/30 bg-white shadow-md">
          {suggestions.data.projects.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => handlePick(p)}
                className="block w-full px-3 py-2 text-left text-sm text-charcoal transition-colors hover:bg-cloud/50"
              >
                {p.name}
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && <div className="mt-1 text-xs text-red-600">{error}</div>}
      {!error && formatIssue && (
        <div className="mt-1 flex items-start gap-1.5 text-xs text-yellow-700">
          <span aria-hidden>⚠</span>
          <span>{formatIssue}</span>
        </div>
      )}
    </div>
  );
}
